import { useMemo } from "react"
import { getSlotLabel } from "@/lib/slot"
import { cn } from "@/lib/utils"
import { AppointmentRow } from "@/modules/waiting-room/components/appointment-table/appointment-row"
import { useAppointmentContext } from "@/modules/waiting-room/contexts/appointment-context"
import {
  WaitingRoomAppointments,
  WaitingRoomSlotAppointment,
} from "@/modules/waiting-room/services/WaitingRoomAppointmentsQuery"
import { DaylightSavingTimeChange } from "@/types/__generated__/union-types"

type AppointmentItemContentProps = {
  numberOfAppointmentsInGroup: number[]
  itemIndex: number
  groupIndex: number
  appointmentGroups: WaitingRoomAppointments
}

type WaitingRoomAppointment = WaitingRoomSlotAppointment & {
  displaySlot?: string
  daylightSavingTimeChange?: DaylightSavingTimeChange
}

const AppointmentItemContent = ({
  appointmentGroups,
  itemIndex,
  groupIndex,
  numberOfAppointmentsInGroup,
}: AppointmentItemContentProps) => {
  const setSelectedWaitingRoomAppointmentId = useAppointmentContext(
    (state) => state.setSelectedWaitingRoomAppointmentId,
  )

  const allAppointmentsOfGroup = useMemo(() => {
    const waitingRoomSlots = appointmentGroups[groupIndex].slots.map((slot) => {
      const updatedWaitingRoomAppointments: WaitingRoomAppointment[] = slot.appointments.map(
        (appointment, appointmentIndex) => {
          const newAppointment: WaitingRoomAppointment = { ...appointment }

          if (appointmentIndex === 0) {
            newAppointment.displaySlot = getSlotLabel(slot.slotId)
            newAppointment.daylightSavingTimeChange = slot.timeChangeStatus
          }

          return newAppointment
        },
      )
      return { ...slot, appointments: updatedWaitingRoomAppointments }
    })

    return waitingRoomSlots.flatMap((slot) => slot?.appointments)
  }, [appointmentGroups, groupIndex])

  const rowIndex = useMemo(() => {
    // Calculate the total number of appointments in all previous groups
    const numberOfAppointmentInPreviousGroups = numberOfAppointmentsInGroup.reduce(
      (total, previousGroupSize, currentIndex) => (currentIndex < groupIndex ? total + previousGroupSize : total),
      0,
    )

    return itemIndex - numberOfAppointmentInPreviousGroups
  }, [itemIndex, groupIndex, numberOfAppointmentsInGroup])

  const appointment = allAppointmentsOfGroup[rowIndex]
  const isLastRowInGroup = rowIndex === numberOfAppointmentsInGroup[groupIndex] - 1

  const handleSelectRow = (row: WaitingRoomAppointment) => {
    setSelectedWaitingRoomAppointmentId(row.id)
  }

  return (
    <div
      className={cn(
        "appointment-row",
        isLastRowInGroup && "pb-8",
        isLastRowInGroup && groupIndex === appointmentGroups.length - 1 && "pb-16",
      )}>
      <div
        data-testid="table-row-container"
        className={cn(
          "border-border bg-surface col-span-full grid divide-y overflow-hidden border border-t-0 text-sm",
          isLastRowInGroup && "rounded-b-xl",
          appointment.displaySlot && "border-t",
          rowIndex === 0 && "border-t-0",
        )}>
        <AppointmentRow
          data-rowid={rowIndex}
          displaySlot={appointment.displaySlot}
          daylightSavingTimeChange={appointment.daylightSavingTimeChange}
          key={appointment.id}
          onClick={() => handleSelectRow(appointment)}
          waitingRoomAppointment={appointment}
          className="grid-cols-appointment"
          data-testid={appointment.id}
        />
      </div>
    </div>
  )
}

export { type WaitingRoomAppointment, AppointmentItemContent }
